/**
 * Pure flow logic for the public single-form page (identity capture × OTP).
 *
 * Kept in a plain .ts module (no React) so the screen-transition + field-layout
 * decisions are unit-testable under node-env vitest — PublicFormPage stays
 * presentational.
 *
 * Semantics (mirror the server's identity config):
 *   - `identityFields` absent (an OLD API without SI deployed) → the DEFAULT trio
 *     (firstName + lastName + dob, all required). The web ships BEFORE the API,
 *     so it must degrade gracefully against a response that omits the field.
 *   - `[]` = no identity step (owner falls back to contact / legacy preview).
 *   - any other array = an explicit, ordered config.
 *
 * OTP forms ALWAYS start on the identity screen (the contact field is needed to
 * send the code); open forms start there only when the config asks for fields.
 */
import type { IdentityFieldDef } from './api.js';
import { DEFAULT_IDENTITY_CONFIG } from './identity-config.js';

export type IdentityFieldKey = IdentityFieldDef['key'];
export type FirstScreen = 'form' | 'identity';

/**
 * Resolve the served identity config the way the server would: an absent field
 * (old API) means DEFAULT. Returns a fresh array; never shares the DEFAULT ref.
 */
export function resolveIdentityConfig(
  identityFields: readonly IdentityFieldDef[] | undefined,
): IdentityFieldDef[] {
  return identityFields ? [...identityFields] : [...DEFAULT_IDENTITY_CONFIG];
}

/** The OTP contact field for an authMode, or null when the form is open. */
export function otpChannelKey(authMode: string): 'email' | 'phone' | null {
  if (authMode === 'otp_email') return 'email';
  if (authMode === 'otp_phone') return 'phone';
  return null;
}

/**
 * First screen after the public schema loads.
 *  - OTP forms → 'identity' (always; the code needs a contact).
 *  - Open forms → 'identity' when the config asks for fields, else 'form'.
 */
export function firstScreen(authMode: string, config: readonly IdentityFieldDef[]): FirstScreen {
  if (otpChannelKey(authMode) !== null) return 'identity';
  return config.length > 0 ? 'identity' : 'form';
}

/** One row on the identity screen. `isOtpChannel` = this input doubles as the
 *  OTP contact field (its value drives request/verify-otp). */
export interface IdentityRow {
  key:          IdentityFieldKey;
  required:     boolean;
  isOtpChannel: boolean;
}

/**
 * The ordered inputs to render on the identity screen: the configured fields,
 * PLUS the OTP contact field for otp_* forms when the config does not already
 * include it. DEDUPE: when the config already contains the OTP channel field,
 * ONE input serves both (that config row is flagged `isOtpChannel`) — no extra
 * row is appended.
 */
export function identityRows(
  authMode: string, config: readonly IdentityFieldDef[],
): IdentityRow[] {
  const otpKey = otpChannelKey(authMode);
  const rows: IdentityRow[] = config.map((f) => ({
    key:          f.key,
    required:     f.required,
    isOtpChannel: f.key === otpKey,
  }));
  if (otpKey !== null && !config.some((f) => f.key === otpKey)) {
    // OTP form whose config omits the channel field → append it (always required
    // and it doubles as the OTP contact input).
    rows.push({ key: otpKey, required: true, isOtpChannel: true });
  }
  return rows;
}
